import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../axiosConfig';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Card,
  CardContent,
  Chip,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';

/**
 * Quarterly Update (MTD ITSA) screen.
 *
 * Lists real ITSAObligation rows, calls calculate-period-summary and
 * submit-period, and only enables submission once a genuine obligation
 * is selected and reviewed.
 *
 * 16 Sept 2026: added the "Held by HMRC" panel, backed by the new
 * /obligations/:id/hmrc-cumulative-summary route (Retrieve a
 * Self-Employment Cumulative Period Summary — mandatory endpoint per
 * HMRC ticket 7626). Fetched when a period is selected and again after
 * a successful submission. Each cumulative submission replaces the last
 * for the tax year, so the customer sees what they're overwriting.
 */
const SelfAssessmentForm = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [hmrcConnected, setHmrcConnected] = useState(false);
  const [obligations, setObligations] = useState([]);
  const [obligationsError, setObligationsError] = useState(null);

  const [selectedObligation, setSelectedObligation] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  // What HMRC currently holds for the selected period's tax year
  const [hmrcHeld, setHmrcHeld] = useState(null);
  const [hmrcHeldLoading, setHmrcHeldLoading] = useState(false);
  const [hmrcHeldError, setHmrcHeldError] = useState(null);

  const [declarationConfirmed, setDeclarationConfirmed] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitResult, setSubmitResult] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const statusResponse = await api.get('/solo/hmrc-itsa/status');
      setHmrcConnected(statusResponse.data.connected);

      if (statusResponse.data.connected) {
        await fetchObligations();
      }
    } catch (error) {
      console.error('Error fetching ITSA status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchObligations = async () => {
    setObligationsError(null);
    try {
      const response = await api.get('/solo/hmrc-itsa/obligations');
      setObligations(response.data.obligations || []);
    } catch (error) {
      console.error('Error fetching obligations:', error);
      setObligationsError(
        error.response?.data?.error || 'Failed to fetch obligations from HMRC'
      );
    }
  };

  const fetchHmrcHeld = async (obligationId) => {
    setHmrcHeldLoading(true);
    setHmrcHeldError(null);
    try {
      const response = await api.get(
        `/solo/hmrc-itsa/obligations/${obligationId}/hmrc-cumulative-summary`
      );
      setHmrcHeld(response.data);
    } catch (error) {
      console.error('Error retrieving HMRC cumulative summary:', error);
      setHmrcHeldError(
        error.response?.data?.error || 'Could not retrieve figures held by HMRC'
      );
      setHmrcHeld(null);
    } finally {
      setHmrcHeldLoading(false);
    }
  };

  const handleSelectObligation = async (obligation) => {
    setSelectedObligation(obligation);
    setSummary(null);
    setSummaryError(null);
    setHmrcHeld(null);
    setHmrcHeldError(null);
    setDeclarationConfirmed(false);
    setSubmitResult(null);
    setSubmitError(null);
    setSummaryLoading(true);

    // Fire the HMRC retrieve in parallel with the local calculation
    fetchHmrcHeld(obligation.id);

    try {
      const response = await api.get(
        `/solo/hmrc-itsa/obligations/${obligation.id}/calculate-period-summary`
      );
      setSummary(response.data);
    } catch (error) {
      console.error('Error calculating period summary:', error);
      const errData = error.response?.data;
      if (errData?.error === 'TURNOVER_THRESHOLD_EXCEEDED') {
        setSummaryError({ type: 'threshold', message: errData.message });
      } else {
        setSummaryError({
          type: 'generic',
          message: errData?.error || 'Failed to calculate figures for this period',
        });
      }
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedObligation || !declarationConfirmed) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const response = await api.post('/solo/hmrc-itsa/submit-period', {
        obligation_id: selectedObligation.id,
        declaration_confirmed: true,
      });
      setSubmitResult(response.data.submission);
      setSubmitDialogOpen(false);
      // Confirm HMRC stored it, then refresh obligations (status can
      // take up to an hour to flip to fulfilled on HMRC's side).
      fetchHmrcHeld(selectedObligation.id);
      fetchObligations();
    } catch (error) {
      console.error('Error submitting period summary:', error);
      const errData = error.response?.data;
      setSubmitError(
        errData?.message || errData?.error || 'HMRC rejected the submission'
      );
      setSubmitDialogOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const money = (n) => `£${Number(n || 0).toLocaleString()}`;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!hmrcConnected) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/solo-dashboard')} sx={{ mb: 3 }}>
          Back to Dashboard
        </Button>
        <Alert severity="warning">
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            HMRC Connection Required
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Connect your HMRC Income Tax account in Tax Settings before you can view or submit quarterly updates.
          </Typography>
          <Button variant="outlined" onClick={() => navigate('/solo/settings')}>
            Go to Tax Settings
          </Button>
        </Alert>
      </Box>
    );
  }

  const openObligations = obligations.filter((o) => o.status.toLowerCase() === 'open');
  const fulfilledObligations = obligations.filter((o) => o.status.toLowerCase() === 'fulfilled');

  const HmrcHeldPanel = () => (
    <Card sx={{ mb: 2, border: 1, borderColor: 'grey.300', bgcolor: 'grey.50' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <CloudDownloadIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Currently held by HMRC{hmrcHeld?.tax_year ? ` — tax year ${hmrcHeld.tax_year}` : ''}
          </Typography>
        </Box>

        {hmrcHeldLoading && <CircularProgress size={20} />}

        {!hmrcHeldLoading && hmrcHeldError && (
          <Typography variant="caption" color="error">{hmrcHeldError}</Typography>
        )}

        {!hmrcHeldLoading && hmrcHeld && !hmrcHeld.exists && (
          <Typography variant="caption" color="text.secondary">
            Nothing submitted for this tax year yet. Your first update will create the record.
          </Typography>
        )}

        {!hmrcHeldLoading && hmrcHeld?.exists && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {hmrcHeld.period_start} to {hmrcHeld.period_end}
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">Turnover</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{money(hmrcHeld.turnover)}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">Expenses</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{money(hmrcHeld.consolidated_expenses)}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">Net profit</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{money(hmrcHeld.net_profit)}</Typography>
              </Grid>
            </Grid>
            {!submitResult && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Submitting replaces these figures for the whole tax year.
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/solo-dashboard')} sx={{ mb: 3 }}>
        Back to Dashboard
      </Button>

      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
              Quarterly Updates
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              Making Tax Digital for Income Tax
            </Typography>
          </Box>
          <Chip icon={<CloudSyncIcon />} label="Connected to HMRC" color="success" />
        </Box>
      </Paper>

      {/* In-year quarterly updates only — SL Solo doesn't yet handle the
          End of Year / Final Declaration step. Shown above the obligation
          list regardless of which period is selected. */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>In-year quarterly updates only.</strong> SL Solo submits your quarterly
          self-employment updates to HMRC via Making Tax Digital. It does not currently handle
          your End of Year declaration or Final Declaration — you'll still need to complete
          that step yourself through HMRC's online service or with an accountant, using the
          figures submitted here as your starting point. End of Year support is planned for a
          future release.
        </Typography>
      </Alert>

      {obligationsError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {obligationsError}
        </Alert>
      )}

      {openObligations.length === 0 && !obligationsError && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No open obligations found. You're either fully up to date, or obligations haven't synced yet.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left: obligation list */}
        <Grid item xs={12} md={5}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Open ({openObligations.length})
          </Typography>
          {openObligations.map((obligation) => (
            <Card
              key={obligation.id}
              sx={{
                mb: 2,
                cursor: 'pointer',
                border: 2,
                borderColor: selectedObligation?.id === obligation.id ? 'primary.main' : 'grey.200',
              }}
              onClick={() => handleSelectObligation(obligation)}
            >
              <CardContent>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {obligation.period_start} to {obligation.period_end}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Due {obligation.due_date}
                </Typography>
              </CardContent>
            </Card>
          ))}

          {fulfilledObligations.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 3, mb: 2, fontWeight: 600, color: 'text.secondary' }}>
                Fulfilled ({fulfilledObligations.length})
              </Typography>
              {fulfilledObligations.map((obligation) => (
                <Card
                  key={obligation.id}
                  sx={{ mb: 2, bgcolor: 'grey.50', cursor: 'pointer' }}
                  onClick={() => handleSelectObligation(obligation)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">
                        {obligation.period_start} to {obligation.period_end}
                      </Typography>
                      <CheckCircleIcon color="success" fontSize="small" />
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </>
          )}
        </Grid>

        {/* Right: selected period detail */}
        <Grid item xs={12} md={7}>
          {!selectedObligation && (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
              <Typography variant="body2" color="text.secondary">
                Select a period on the left to review its figures.
              </Typography>
            </Paper>
          )}

          {selectedObligation && <HmrcHeldPanel />}

          {selectedObligation && summaryLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {selectedObligation && summaryError?.type === 'threshold' && (
            <Alert severity="warning">
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Turnover over £90,000 — itemised submission required
              </Typography>
              <Typography variant="body2">
                This period can't be submitted through this screen yet. Your rolling 12-month turnover
                is above HMRC's threshold for consolidated (single-total) expense reporting, and
                itemised category submission isn't built yet. Contact support for now.
              </Typography>
            </Alert>
          )}

          {selectedObligation && summaryError?.type === 'generic' && (
            <Alert severity="error">{summaryError.message}</Alert>
          )}

          {selectedObligation && summary && !submitResult && (
            <Card sx={{ border: 2, borderColor: 'primary.main' }}>
              <CardContent>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
                  Your figures — {summary.period_start} to {summary.period_end}
                </Typography>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Box sx={{ p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">Turnover</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {money(summary.turnover)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ p: 2, bgcolor: 'error.lighter', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">Expenses</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {money(summary.consolidated_expenses)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">Net Profit</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {money(summary.net_profit)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="caption">
                    Reported as consolidated expenses (single total, no category breakdown) — the
                    correct method for turnover under £90,000. Figures are calculated from your
                    income and expense records for this period only.
                  </Typography>
                </Alert>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={declarationConfirmed}
                      onChange={(e) => setDeclarationConfirmed(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I confirm these figures are correct to the best of my knowledge and I want to
                      submit this quarterly update to HMRC.
                    </Typography>
                  }
                />

                {submitError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {submitError}
                  </Alert>
                )}

                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  startIcon={<SendIcon />}
                  sx={{ mt: 2 }}
                  disabled={!declarationConfirmed}
                  onClick={() => setSubmitDialogOpen(true)}
                >
                  Submit to HMRC
                </Button>
              </CardContent>
            </Card>
          )}

          {submitResult && (
            <Alert severity="success" icon={<CheckCircleIcon />}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Quarterly update submitted
              </Typography>
              <Typography variant="body2">
                Net profit reported: {money(submitResult.net_profit)}
              </Typography>
              {submitResult.hmrc_submission_id && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Reference: {submitResult.hmrc_submission_id}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                The "Currently held by HMRC" panel above has been refreshed from HMRC to confirm.
              </Typography>
            </Alert>
          )}
        </Grid>
      </Grid>

      <Dialog open={submitDialogOpen} onClose={() => setSubmitDialogOpen(false)}>
        <DialogTitle>Confirm submission to HMRC</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will submit your figures for {selectedObligation?.period_start} to{' '}
            {selectedObligation?.period_end} directly to HMRC and replace any figures HMRC currently
            holds for this tax year.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubmitDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <CircularProgress size={20} /> : 'Confirm & Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SelfAssessmentForm;